/**
 * AppDrawer - Responsive drawer component
 * 
 * Mobile: Bottom sheet with drag-to-close
 * Desktop: Side drawer
 * 
 * Supports dirty state tracking: when `isDirty` is true,
 * closing attempts show a discard confirmation before actually closing.
 */

import React, { useRef, useState, useCallback, useEffect } from "react";
import {
  Drawer,
  Group,
  Button,
  Text,
  Box,
  Alert,
  LoadingOverlay,
  ScrollArea,
  Modal,
  MantineSize,
} from "@mantine/core";
import { useTranslation } from "react-i18next";

import { useAppEnvironment } from "@/contexts/AppEnvironmentContext";

import { AppIcon } from "../AppIcon";

// ============================================
// Types
// ============================================

export type DrawerActionType = "save-cancel" | "confirm-cancel" | "close" | "custom";

export interface AppDrawerAction {
  label: string;
  onClick: () => void;
  variant?: "primary" | "secondary" | "danger";
  iconName?: string;
  disabled?: boolean;
  loading?: boolean;
}

export interface AppDrawerProps {
  opened: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  children?: React.ReactNode;
  closable?: boolean;
  loading?: boolean;
  errors?: string | string[] | null;
  actionType?: DrawerActionType;
  onConfirm?: () => void;
  confirmLabel?: string;
  confirmIcon?: string;
  confirmDisabled?: boolean;
  confirmColor?: string;
  cancelLabel?: string;
  customActions?: React.ReactNode;
  leftActions?: AppDrawerAction[];
  position?: "left" | "right" | "top" | "bottom";
  size?: MantineSize | string | number;
  responsive?: boolean;
  mobileHeightPercent?: number;
  mobileBreakpoint?: number;

  /** When true, closing attempts show a discard confirmation */
  isDirty?: boolean;
}

// ============================================
// Constants
// ============================================

const CLOSE_THRESHOLD = 100;

// ============================================
// Component
// ============================================

export const AppDrawer: React.FC<AppDrawerProps> = ({
  opened,
  onClose,
  title,
  children,
  closable = true,
  loading = false,
  errors,
  actionType = "close",
  onConfirm,
  confirmLabel,
  confirmIcon,
  confirmDisabled = false,
  confirmColor,
  cancelLabel,
  customActions,
  leftActions,
  position = "right",
  size = "lg",
  responsive = true,
  mobileHeightPercent = 92,
  isDirty = false,
}) => {
  const { t } = useTranslation("common");

  // Responsive detection
  const { isMobile } = useAppEnvironment();
  const useBottomSheet = responsive && isMobile;

  // Drawer config based on viewport
  const drawerPosition = useBottomSheet ? "bottom" : position;
  const drawerSize = useBottomSheet ? `${mobileHeightPercent}%` : size;

  // Drag state
  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [closingByDrag, setClosingByDrag] = useState(false);

  // Discard confirmation state
  const [discardConfirmOpened, setDiscardConfirmOpened] = useState(false);

  const dragStartY = useRef(0);

  // Ref to Mantine's drawer content element for direct DOM manipulation
  const drawerContentRef = useRef<HTMLDivElement | null>(null);

  // ============================================
  // Guarded close: intercept if dirty
  // ============================================

  const handleCloseAttempt = useCallback(() => {
    if (isDirty) {
      setDiscardConfirmOpened(true);
    } else {
      onClose();
    }
  }, [isDirty, onClose]);

  const handleConfirmDiscard = useCallback(() => {
    // Close modal first, then drawer after modal's close animation.
    // This lets Mantine restore scroll state on the drawer
    // before the drawer itself closes with its own animation.
    setDiscardConfirmOpened(false);
    onClose();
  }, [onClose]);

  const handleCancelDiscard = useCallback(() => {
    setDiscardConfirmOpened(false);
  }, []);

  // Reset discard modal when drawer closes
  useEffect(() => {
    if (!opened) {
      setDiscardConfirmOpened(false);
    }
  }, [opened]);

  // Find and store ref to Mantine's drawer content when opened
  // Don't set any styles initially - let Mantine's open animation play
  useEffect(() => {
    if (opened && useBottomSheet) {
      // Reset the closing flag
      setClosingByDrag(false);

      // Wait for Mantine's open animation to complete before we start tracking
      const timer = setTimeout(() => {
        const content = document.querySelector('.mantine-Drawer-content') as HTMLDivElement;
        if (content) {
          drawerContentRef.current = content;
        }
      }, 300); // Mantine's default animation is ~200-300ms
      return () => clearTimeout(timer);
    }
    return;
  }, [opened, useBottomSheet]);

  // Apply transform directly to Mantine's drawer content during drag only
  useEffect(() => {
    const content = drawerContentRef.current;
    if (!content || !useBottomSheet || !isDragging) return;

    if (dragY > 0) {
      content.style.transitionDuration = '0ms';
      content.style.transform = `translateY(${dragY}px)`;
    }
  }, [dragY, isDragging, useBottomSheet]);

  // Handle snap-back animation when drag ends without closing
  useEffect(() => {
    const content = drawerContentRef.current;
    if (!content || !useBottomSheet) return;

    if (!isDragging && !closingByDrag && dragY === 0) {
      content.style.removeProperty('transform');
      content.style.removeProperty('transition-duration');
      content.style.removeProperty('transition-timing-function');
    }
  }, [isDragging, dragY, useBottomSheet, closingByDrag]);

  // Clean up ref when drawer closes
  useEffect(() => {
    if (!opened) {
      // Small delay to let close animation finish
      const timer = setTimeout(() => {
        if (drawerContentRef.current) {
          drawerContentRef.current.style.transform = '';
          drawerContentRef.current.style.transitionDuration = '';
        }

        drawerContentRef.current = null;
        setDragY(0);
        setIsDragging(false);
        setClosingByDrag(false);
      }, 300);
      return () => clearTimeout(timer);
    }
    return;
  }, [opened]);

  // ============================================
  // Drag Handlers using Pointer Events
  // (works for both touch and mouse)
  // ============================================

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    if (!closable || loading) return;

    // Capture pointer for tracking outside element
    (e.target as HTMLElement).setPointerCapture(e.pointerId);

    dragStartY.current = e.clientY;
    setIsDragging(true);
    setDragY(0);
  }, [closable, loading]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDragging) return;

    const delta = e.clientY - dragStartY.current;
    // Only allow dragging down
    if (delta > 0) {
      setDragY(delta);
    }
  }, [isDragging]);

  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    if (!isDragging) return;

    // Release pointer capture
    (e.target as HTMLElement).releasePointerCapture(e.pointerId);

    setIsDragging(false);

    if (dragY > CLOSE_THRESHOLD) {
      // If dirty, snap back and show confirmation instead of closing
      if (isDirty) {
        setDragY(0);
        setDiscardConfirmOpened(true);
        return;
      }

      // Mark that we're closing by drag - prevents snap-back
      setClosingByDrag(true);

      // Animate drawer off-screen
      const content = drawerContentRef.current;
      if (content) {
        content.style.transitionDuration = '200ms';
        content.style.transitionTimingFunction = 'ease-out';
        content.style.transform = 'translateY(100%)';
      }

      // Close after animation completes
      setTimeout(() => {
        onClose();
      }, 200);
    } else {
      // Snap back to original position
      setDragY(0);
    }
  }, [isDragging, dragY, isDirty, onClose]);

  // ============================================
  // Error messages
  // ============================================

  const errorMessages = errors
    ? Array.isArray(errors) ? errors : [errors]
    : [];

  // ============================================
  // Left actions renderer
  // ============================================

  const renderLeftActions = () => {
    if (!leftActions || leftActions.length === 0) return null;

    return (
      <Group gap="sm" wrap="nowrap">
        {leftActions.map((action, index) => {
          const buttonVariant = action.variant === "danger"
            ? "filled"
            : action.variant === "primary"
              ? "filled"
              : "default";

          const buttonColor = action.variant === "danger"
            ? "red"
            : action.variant === "primary"
              ? undefined
              : undefined;

          return (
            <Button
              key={index}
              variant={buttonVariant}
              color={buttonColor}
              size="lg"
              onClick={action.onClick}
              disabled={action.disabled || loading}
              loading={action.loading}
              leftSection={
                action.iconName && !action.loading ? (
                  <AppIcon iconName={action.iconName} size={18} />
                ) : undefined
              }
            >
              {action.label}
            </Button>
          );
        })}
      </Group>
    );
  };

  // ============================================
  // Action buttons renderer
  // ============================================

  const renderActions = () => {
    if (actionType === "custom") {
      return customActions;
    }

    if (actionType === "close") {
      return (
        <Button
          variant="default"
          size="lg"
          onClick={handleCloseAttempt}
          disabled={loading}
          fullWidth={useBottomSheet}
        >
          {cancelLabel || t("buttons.close")}
        </Button>
      );
    }

    const isSaveCancel = actionType === "save-cancel";
    const primaryLabel = confirmLabel || (isSaveCancel ? t("buttons.save") : t("buttons.confirm"));
    const secondaryLabel = cancelLabel || t("buttons.cancel");

    return (
      <Group justify="flex-end" gap="sm" grow={useBottomSheet}>
        <Button variant="default" onClick={handleCloseAttempt} disabled={loading} size="lg">
          {secondaryLabel}
        </Button>
        <Button
          onClick={onConfirm}
          loading={loading}
          disabled={confirmDisabled}
          size="lg"
          color={confirmColor}
          leftSection={confirmIcon && !loading ? <AppIcon iconName={confirmIcon} size={18} /> : undefined}
        >
          {primaryLabel}
        </Button>
      </Group>
    );
  };

  const showActions = actionType !== "close" || closable;
  const hasLeftActions = leftActions && leftActions.length > 0;

  // Calculate visual feedback values
  const dragProgress = Math.min(dragY / CLOSE_THRESHOLD, 1);
  const handleScale = 1 + (dragProgress * 0.3);
  const handleColor = isDragging
    ? `rgba(0, 0, 0, ${0.2 + dragProgress * 0.3})`
    : "rgba(0, 0, 0, 0.2)";

  // ============================================
  // Render
  // ============================================

  return (
    <>
      <Drawer
        opened={opened}
        onClose={handleCloseAttempt}
        position={drawerPosition}
        size={drawerSize}
        // Don't use Mantine's header - we render our own inside the transform wrapper
        title={undefined}
        withCloseButton={false}
        closeOnClickOutside={closable && !isDragging}
        closeOnEscape={closable}
        padding={0}
        // Disable Mantine's transition when we're closing by drag (we handle it ourselves)
        transitionProps={{
          duration: closingByDrag ? 0 : 200,
        }}
        styles={{
          content: {
            borderTopLeftRadius: useBottomSheet ? 16 : 0,
            borderTopRightRadius: useBottomSheet ? 16 : 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          },
          header: {
            display: "none", // Hide Mantine's header completely
          },
          body: {
            padding: 0,
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            minHeight: 0,
          },
        }}
      >
        {/* Loading overlay */}
        <LoadingOverlay visible={loading} zIndex={1000} />

        {/* 
          Content wrapper - transform is applied directly to Mantine's 
          .mantine-Drawer-content via ref for proper visual feedback
        */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
          }}
        >

          {/* ====== DRAG HANDLE (mobile only) ====== */}
          {useBottomSheet && (
            <Box
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerUp}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "12px 0 8px 0",
                cursor: closable && !loading ? "grab" : "default",
                touchAction: "none",
                userSelect: "none",
                flexShrink: 0,
              }}
            >
              {/* Handle bar with visual feedback */}
              <Box
                style={{
                  width: 40,
                  height: 5,
                  borderRadius: 3,
                  backgroundColor: handleColor,
                  transform: `scaleX(${handleScale})`,
                  transitionProperty: isDragging ? "none" : "transform, background-color",
                  transitionDuration: "200ms",
                  transitionTimingFunction: "ease-out",
                }}
              />
              {/* Hint text when dragging past threshold */}
              {dragY > CLOSE_THRESHOLD * 0.7 && (
                <Text
                  size="xs"
                  c="dimmed"
                  mt={4}
                  style={{
                    opacity: Math.min((dragY - CLOSE_THRESHOLD * 0.7) / (CLOSE_THRESHOLD * 0.3), 1),
                    transitionProperty: "opacity",
                    transitionDuration: "100ms",
                  }}
                >
                  Release to close
                </Text>
              )}
            </Box>
          )}

          {/* ====== CUSTOM HEADER (title + close button) ====== */}
          {(title || closable) && (
            <Box
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "16px",
                paddingTop: useBottomSheet ? 8 : 16,
                flexShrink: 0,
              }}
            >
              <Text fw={600} size="lg">
                {title}
              </Text>
              {closable && (
                <Box
                  component="button"
                  onClick={handleCloseAttempt}
                  disabled={loading}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: loading ? "default" : "pointer",
                    padding: 4,
                    borderRadius: 4,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "var(--mantine-color-gray-6)",
                    opacity: loading ? 0.5 : 1,
                  }}
                  aria-label="Close"
                >
                  <AppIcon iconName="X" size={20} />
                </Box>
              )}
            </Box>
          )}

          {/* ====== SCROLLABLE CONTENT using Mantine ScrollArea ====== */}
          <ScrollArea
            style={{ flex: 1 }}
            type="auto"
            offsetScrollbars
            scrollbarSize={8}
          >
            <Box px="md" pb="md">
              {children}
            </Box>
          </ScrollArea>

          {/* ====== ERROR MESSAGES ====== */}
          {errorMessages.length > 0 && (
            <Box px="md" style={{ flexShrink: 0 }}>
              <Alert
                color="red"
                icon={<AppIcon iconName="AlertCircle" size={16} />}
                mb="md"
              >
                {errorMessages.map((msg, i) => (
                  <Text key={i} size="sm">{msg}</Text>
                ))}
              </Alert>
            </Box>
          )}

          {/* ====== ACTION BUTTONS (sticky footer) ====== */}
          {showActions && (
            <Box
              style={{
                flexShrink: 0,
                borderTop: "1px solid var(--mantine-color-gray-3)",
                padding: 16,
                paddingBottom: useBottomSheet
                  ? "calc(16px + env(safe-area-inset-bottom, 0px))"
                  : 16,
                backgroundColor: "var(--mantine-color-body)",
              }}
            >
              {hasLeftActions ? (
                <Group justify="space-between" wrap="nowrap">
                  {renderLeftActions()}
                  {renderActions()}
                </Group>
              ) : (
                renderActions()
              )}
            </Box>
          )}

        </div>
      </Drawer>

      {/* ====== DISCARD CONFIRMATION MODAL ====== */}
      <Modal
        opened={discardConfirmOpened}
        onClose={handleCancelDiscard}
        title={t("unsavedChanges.title", "Unsaved Changes")}
        centered
        size="sm"
        zIndex={1000}
      >
        <Text size="sm" mb="lg">
          {t(
            "unsavedChanges.message",
            "You have unsaved changes. Are you sure you want to discard them?"
          )}
        </Text>
        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={handleCancelDiscard}>
            {t("unsavedChanges.keepEditing", "Keep Editing")}
          </Button>
          <Button color="red" onClick={handleConfirmDiscard}>
            {t("unsavedChanges.discard", "Discard")}
          </Button>
        </Group>
      </Modal>
    </>
  );
};